import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, type UserSettings } from "../lib/api.js";
import { signOut } from "../lib/auth.js";
import { AuthGate } from "../components/AuthGate.js";
import { ErrorText, Field } from "../components/ui.js";
import { RewardButton } from "../components/RewardButton.js";
import { enablePush, pushStatus, sendTestPush } from "../lib/push.js";
import { setAdFreeUntil } from "../lib/ads.js";

export function SettingsPage() {
  return (
    <section>
      <h1>設定</h1>
      <AuthGate>
        <SettingsInner />
      </AuthGate>
      <h2>規約など</h2>
      <ul className="list">
        <li>
          <Link to="/legal/terms">利用規約</Link>
        </li>
        <li>
          <Link to="/legal/privacy">プライバシーポリシー</Link>
        </li>
        <li>
          <Link to="/legal/disclaimer">免責事項</Link>
        </li>
      </ul>
    </section>
  );
}

function SettingsInner() {
  const navigate = useNavigate();
  const [s, setS] = useState<UserSettings | null>(null);
  const [tax, setTax] = useState("20.315");
  const [hour, setHour] = useState("8");
  const [expiryDays, setExpiryDays] = useState("30,7");
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);
  const [pushMsg, setPushMsg] = useState<string | null>(null);
  const [subs, setSubs] = useState<Array<{ id: string; platform: string; createdAt: string }>>([]);
  const reloadSubs = () => api.pushSubscriptions().then((r) => setSubs(r.subscriptions)).catch(() => {});

  useEffect(() => {
    api
      .settings()
      .then((r) => {
        setS(r);
        setTax(String(r.dividendTaxRate * 100));
        setHour(String(r.notifyHour));
        setExpiryDays(r.notifyExpiryDays.join(","));
      })
      .catch(setError);
    void reloadSubs();
  }, []);

  if (!s) return <p className="muted">読み込み中…</p>;

  const save = async () => {
    setError(null);
    setSaved(false);
    try {
      await api.updateSettings({
        dividendTaxRate: Number(tax) / 100,
        notifyLastTradingDay: s.notifyLastTradingDay,
        notifyExDate: s.notifyExDate,
        notifyHour: Number(hour),
        notifyExpiryDays: expiryDays
          .split(",")
          .map((v) => Number(v.trim()))
          .filter((n) => Number.isInteger(n) && n >= 0),
        showUsedYutai: s.showUsedYutai,
      });
      setSaved(true);
    } catch (e) {
      setError(e);
    }
  };

  const onImport = async (file: File | undefined) => {
    if (!file) return;
    setImportResult(null);
    setError(null);
    try {
      const body = JSON.parse(await file.text()) as unknown;
      const r = await api.importBackup(body);
      setImportResult(Object.entries(r.counts).map(([k, v]) => `${k}: ${v}`).join(", "));
    } catch (e) {
      setError(e);
    }
  };

  const deleteAccount = async () => {
    if (!confirm("アカウントと全データを削除します。元に戻せません。よろしいですか？")) return;
    if (!confirm("本当に削除しますか？")) return;
    await api.deleteAccount();
    await signOut();
    navigate("/");
  };

  return (
    <>
      <div className="card form">
        <h2>計算</h2>
        <Field label="配当の源泉徴収率（%）">
          <input className="input" inputMode="decimal" value={tax} onChange={(e) => setTax(e.target.value)} />
        </Field>
        <p className="muted">
          証券会社ごとの料率は <Link to="/my/brokers">証券会社設定</Link> で編集します。
        </p>
      </div>
      <div className="card form">
        <h2>通知</h2>
        <label className="check">
          <input type="checkbox" checked={s.notifyLastTradingDay} onChange={(e) => setS({ ...s, notifyLastTradingDay: e.target.checked })} /> 権利付最終日の前営業日に通知
        </label>
        <label className="check">
          <input type="checkbox" checked={s.notifyExDate} onChange={(e) => setS({ ...s, notifyExDate: e.target.checked })} /> 権利落ち日の朝に現渡しを通知
        </label>
        <Field label="優待期限の通知（何日前。カンマ区切り）">
          <input className="input" value={expiryDays} onChange={(e) => setExpiryDays(e.target.value)} />
        </Field>
        <Field label="通知時刻（0〜23 時、日本時間）">
          <input className="input" type="number" min={0} max={23} value={hour} onChange={(e) => setHour(e.target.value)} />
        </Field>
        <label className="check">
          <input type="checkbox" checked={s.showUsedYutai} onChange={(e) => setS({ ...s, showUsedYutai: e.target.checked })} /> 優待一覧で使用済みも表示
        </label>
        <div className="card">
          <h2>プッシュ通知の受信</h2>
          <p className="muted">
            この端末: {{ unsupported: "非対応", denied: "ブロック中", default: "未設定", granted: "許可済み" }[pushStatus()]} / 登録済み端末 {subs.length}
          </p>
          <div className="row wrap">
            <button
              className="btn primary"
              type="button"
              onClick={async () => {
                setPushMsg(null);
                try {
                  await enablePush();
                  setPushMsg("この端末で通知を受け取れるようになりました。");
                  await reloadSubs();
                } catch (e) {
                  setPushMsg(e instanceof Error ? e.message : String(e));
                }
              }}
            >
              この端末で通知を受け取る
            </button>
            <button
              className="btn"
              type="button"
              disabled={subs.length === 0}
              onClick={async () => {
                try {
                  const r = await sendTestPush();
                  setPushMsg(`テスト通知: 送信 ${r.sent} / 失敗 ${r.failed} / 未対応 ${r.skipped}`);
                } catch (e) {
                  setPushMsg(e instanceof Error ? e.message : String(e));
                }
              }}
            >
              テスト通知を送る
            </button>
          </div>
          {pushMsg && <p className="muted">{pushMsg}</p>}
          {subs.length > 0 && (
            <ul className="list">
              {subs.map((x) => (
                <li key={x.id} className="row between">
                  <span>
                    {x.platform} / {x.createdAt.slice(0, 10)}
                  </span>
                  <button
                    className="btn small"
                    type="button"
                    onClick={async () => {
                      await api.deletePushSubscription(x.id);
                      await reloadSubs();
                    }}
                  >
                    解除
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <ErrorText error={error} />
      {saved && <p className="muted">保存しました。</p>}
      <button className="btn primary" type="button" onClick={save}>
        保存
      </button>

      <div className="card form">
        <h2>広告</h2>
        <p className="muted">{s.adFreeUntil && s.adFreeUntil > new Date().toISOString() ? `広告非表示中（${s.adFreeUntil.slice(0, 16).replace("T", " ")} まで）` : "広告を表示しています。"}</p>
        <RewardButton
          kind="ad_free"
          label="24時間広告を非表示にする"
          onGranted={(until) => {
            setAdFreeUntil(until);
            setS({ ...s, adFreeUntil: until });
          }}
        />
      </div>

      <div className="card form">
        <h2>バックアップ</h2>
        <a className="btn" href={api.exportUrl()} download>
          JSON をエクスポート
        </a>
        <Field label="JSON をインポート（同じ ID は上書き、削除はしない）">
          <input className="input" type="file" accept="application/json,.json" onChange={(e) => onImport(e.target.files?.[0])} />
        </Field>
        {importResult && <p className="muted">取り込み: {importResult}</p>}
      </div>

      <div className="card form">
        <h2>アカウント</h2>
        <button className="btn danger" type="button" onClick={deleteAccount}>
          アカウントを削除
        </button>
      </div>
    </>
  );
}
